import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskMode } from 'common/types';
import { Comment, CommentSourceType } from '@/types';

import { RootState } from './rootReducer';

export type CommentDrawerType = CommentSourceType | 'resource-tree' | undefined;
interface CommentInitialState {
  curComment: Comment | null;
  groupedComments?: Record<string, Comment[]>;
  isRightExpanded?: boolean;
  isCommentDrawerOpen: boolean;
  commentDrawerType?: CommentDrawerType;
  helpPanelOpen?: boolean;
  previewMode: TaskMode;
  isImageDrawerOpen: boolean;
  imageUrl: string;
  filteredCommentList: Comment[];
  needsRebuildAnnotation?: boolean;
  curCommentId: number;
  curCommentIndex: number;
  isInspecting: boolean;
}

const initialState: CommentInitialState = {
  curComment: null,
  isRightExpanded: false,
  isCommentDrawerOpen: false,
  commentDrawerType: undefined,
  helpPanelOpen: false,
  previewMode: TaskMode.Preview,
  isImageDrawerOpen: false,
  imageUrl: '',
  filteredCommentList: [],
  needsRebuildAnnotation: false,
  curCommentId: -1,
  curCommentIndex: -1,
  isInspecting: true,
};

const comment = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    setCurComment(state, { payload }: PayloadAction<Comment | null>) {
      state.curComment = payload;
    },
    setGroupedComments(state, { payload }: PayloadAction<Record<string, Comment[]>>) {
      state.groupedComments = { ...payload };
    },
    setIsRightExpanded(state, { payload }: PayloadAction<boolean>) {
      state.isRightExpanded = payload;
    },
    setIsCommentDrawerOpen(state, { payload }: PayloadAction<boolean>) {
      state.isCommentDrawerOpen = payload;
    },
    setCommentDrawerType(state, { payload }: PayloadAction<CommentDrawerType>) {
      state.commentDrawerType = payload;
    },
    setHelpPanelOpen(state, { payload }: PayloadAction<boolean>) {
      state.helpPanelOpen = payload;
    },
    setPreviewMode(state, { payload }: PayloadAction<TaskMode>) {
      state.previewMode = payload;
    },
    setIsImageDrawerOpen(state, { payload }: PayloadAction<boolean>) {
      state.isImageDrawerOpen = payload;
    },
    setImageUrl(state, { payload }: PayloadAction<string>) {
      state.imageUrl = payload;
    },
    setFilteredCommentList(state, { payload }: PayloadAction<Comment[]>) {
      state.filteredCommentList = payload;
    },
    setNeedsRebuildAnnotation(state, { payload }: PayloadAction<boolean>) {
      state.needsRebuildAnnotation = payload;
    },
    setCurCommentId(state, { payload }: PayloadAction<number>) {
      state.curCommentId = payload;
    },
    setCurrentCommentIndex(state, { payload }: PayloadAction<number>) {
      state.curCommentIndex = payload;
    },
    setIsInspecting(state, { payload }: PayloadAction<boolean>) {
      state.isInspecting = payload;
    },
  },
});

export const {
  setCurComment,
  setGroupedComments,
  setIsRightExpanded,
  setIsCommentDrawerOpen,
  setCommentDrawerType,
  setHelpPanelOpen,
  setPreviewMode,
  setIsImageDrawerOpen,
  setImageUrl,
  setFilteredCommentList,
  setNeedsRebuildAnnotation,
  setCurCommentId,
  setCurrentCommentIndex,
  setIsInspecting,
} = comment.actions;
export const selectCurComment = (state: RootState) => state.comment.curComment;
export const selectGroupedComments = (state: RootState) => state.comment.groupedComments;
export const selectIsRightExpanded = (state: RootState) => state.comment.isRightExpanded;
export const selectIsCommentDrawerOpen = (state: RootState) => state.comment.isCommentDrawerOpen;
export const selectCommentDrawerType = (state: RootState) => state.comment.commentDrawerType;
export const selectHelpPanelOpen = (state: RootState) => state.comment.helpPanelOpen;
export const selectPreviewMode = (state: RootState) => state.comment.previewMode;
export const selectIsImageDrawerOpen = (state: RootState) => state.comment.isImageDrawerOpen;
export const selectImageUrl = (state: RootState) => state.comment.imageUrl;
export const selectFilteredCommentList = (state: RootState) => state.comment.filteredCommentList;
export const selectNeedsRebuildAnnotation = (state: RootState) => state.comment.needsRebuildAnnotation;
export const selectCurCommentId = (state: RootState) => state.comment.curCommentId;
export const selectCurrentCommentIndex = (state: RootState) => state.comment.curCommentIndex;
export const selectIsInspecting = (state: RootState) => state.comment.isInspecting;

export default comment.reducer;
